// src/components/backend/companies/sections/SectionBilder.jsx
import React from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Typography,
    TextField,
} from "@mui/material";
import {
    Image as ImageIcon,
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    Delete as DeleteIcon,
    CenterFocusStrong as CenterFocusStrongIcon,
} from "@mui/icons-material";

import { cropImageStyle, normalizeImageCrop, resolveUrl, sortImagesLocal } from "../utils/companyUtils";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const START_IMAGE_ASPECT = 2.5;

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

function cropToSelection(crop) {
    const c = normalizeImageCrop(crop);
    const aspect = c.aspect || START_IMAGE_ASPECT;
    let width = clamp(100 / c.zoom, 28, 100);
    let height = width / aspect;

    if (height > 100) {
        height = 100;
        width = height * aspect;
    }

    return {
        x: clamp(c.x - width / 2, 0, 100 - width),
        y: clamp(c.y - height / 2, 0, 100 - height),
        width,
        height,
    };
}

function selectionToCrop(selection) {
    return normalizeImageCrop({
        x: selection.x + selection.width / 2,
        y: selection.y + selection.height / 2,
        zoom: 100 / selection.width,
        aspect: START_IMAGE_ASPECT,
    });
}

function normalizeSelection(selection) {
    let width = clamp(selection.width, 28, 100);
    let height = width / START_IMAGE_ASPECT;

    if (height > 100) {
        height = 100;
        width = height * START_IMAGE_ASPECT;
    }

    return {
        x: clamp(selection.x, 0, 100 - width),
        y: clamp(selection.y, 0, 100 - height),
        width,
        height,
    };
}

export default function SectionBilder({
    company,
    imageSelection,
    submitting,
    sortingImages,
    sortImagesError,
    onDeleteImage,
    onSetStartImage,
    onSaveImageCrop,
    onMoveOrderedIds,
}) {
    const [dragActiveExtra, setDragActiveExtra] = React.useState(false);
    const [dragActiveStart, setDragActiveStart] = React.useState(false);

    const [draggedId, setDraggedId] = React.useState(null);
    const [dragOverId, setDragOverId] = React.useState(null);
    const [localOrderedImages, setLocalOrderedImages] = React.useState([]);
    const [dragPreview, setDragPreview] = React.useState(null);
    const [cropDialog, setCropDialog] = React.useState(null);
    const [cropDrag, setCropDrag] = React.useState(null);
    const cropImageRef = React.useRef(null);

    const sortedExistingImages = React.useMemo(() => {
        return sortImagesLocal(company?.images || []);
    }, [company]);

    React.useEffect(() => {
        setLocalOrderedImages(sortedExistingImages);
    }, [sortedExistingImages]);

    // STARTBILD DROP
    const handleStartDrop = (e) => {
        e.preventDefault();
        setDragActiveStart(false);
        const file = e.dataTransfer.files?.[0];
        if (file) imageSelection.pickStartImage(file);
    };

    // EXTRA DROP
    const handleExtraDrop = (e) => {
        e.preventDefault();
        setDragActiveExtra(false);
        const files = e.dataTransfer.files;
        if (files?.length) imageSelection.addExtraImages(files);
    };

    // DRAG SORT
    const handleDragStart = (e, img) => {
        setDraggedId(img.id);

        setDragPreview({
            src: resolveUrl(img?.url, API_BASE),
            x: e.clientX,
            y: e.clientY,
        });

        const empty = document.createElement("div");
        empty.style.width = "1px";
        empty.style.height = "1px";
        e.dataTransfer.setDragImage(empty, 0, 0);
    };

    const handleDragOverItem = (e, targetId) => {
        e.preventDefault();

        setDragPreview((prev) =>
            prev ? { ...prev, x: e.clientX, y: e.clientY } : prev
        );

        if (!draggedId || draggedId === targetId) return;

        setDragOverId(targetId);

        setLocalOrderedImages((prev) => {
            const from = prev.findIndex((x) => x.id === draggedId);
            const to = prev.findIndex((x) => x.id === targetId);

            if (from === -1 || to === -1 || from === to) return prev;

            const next = [...prev];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return next;
        });
    };

    const handleDropExisting = () => {
        onMoveOrderedIds?.(localOrderedImages.map((x) => x.id));
        setDraggedId(null);
        setDragOverId(null);
        setDragPreview(null);
    };

    // MANUAL ORDER EXISTING
    const handleManualOrder = (id, value) => {
        let pos = parseInt(value);
        if (isNaN(pos)) return;

        const list = [...localOrderedImages];
        const from = list.findIndex((i) => i.id === id);
        if (from === -1) return;

        pos = Math.max(1, Math.min(list.length, pos)) - 1;

        const updated = [...list];
        const [moved] = updated.splice(from, 1);
        updated.splice(pos, 0, moved);

        setLocalOrderedImages(updated);
        onMoveOrderedIds(updated.map((x) => x.id));
    };

    const openNewStartCrop = () => {
        if (!imageSelection.startImagePreview) return;
        setCropDialog({
            mode: "new",
            title: "Ausschnitt für neues Startbild",
            src: imageSelection.startImagePreview,
            crop: normalizeImageCrop(imageSelection.startImageCrop),
            selection: cropToSelection(imageSelection.startImageCrop),
        });
    };

    const openExistingCrop = (img) => {
        setCropDialog({
            mode: "existing",
            imageId: img.id,
            title: company?.start_image_id === img.id ? "Startbild-Ausschnitt" : "Ausschnitt festlegen",
            src: resolveUrl(img?.url, API_BASE),
            crop: normalizeImageCrop(img),
            selection: cropToSelection(img),
        });
    };

    const saveCropDialog = () => {
        if (!cropDialog) return;
        const crop = selectionToCrop(cropDialog.selection);
        if (cropDialog.mode === "new") {
            imageSelection.setStartImageCrop(crop);
        } else if (cropDialog.imageId) {
            if (company?.start_image_id === cropDialog.imageId) {
                onSaveImageCrop?.(cropDialog.imageId, crop);
            } else {
                onSetStartImage?.(cropDialog.imageId, crop);
            }
        }
        setCropDialog(null);
        setCropDrag(null);
    };

    const updateCropSelection = (selection) => {
        setCropDialog((prev) => {
            if (!prev) return prev;
            const next = normalizeSelection(selection);
            return {
                ...prev,
                selection: next,
                crop: selectionToCrop(next),
            };
        });
    };

    const pointFromEvent = (e, rect) => ({
        x: clamp(((e.clientX - rect.left) / rect.width) * 100, 0, 100),
        y: clamp(((e.clientY - rect.top) / rect.height) * 100, 0, 100),
    });

    const handleCropPointerDown = (e, mode = "draw") => {
        if (!cropDialog) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture?.(e.pointerId);

        const rect = cropImageRef.current?.getBoundingClientRect();
        if (!rect) return;
        const point = pointFromEvent(e, rect);

        setCropDrag({
            mode,
            rect,
            start: point,
            initial: cropDialog.selection,
        });

        if (mode === "draw") {
            const width = 36;
            const height = width / START_IMAGE_ASPECT;
            updateCropSelection({
                x: clamp(point.x - width / 2, 0, 100 - width),
                y: clamp(point.y - height / 2, 0, 100 - height),
                width,
                height,
            });
        }
    };

    const handleCropPointerMove = (e) => {
        if (!cropDrag) return;

        const point = pointFromEvent(e, cropDrag.rect);
        const initial = cropDrag.initial;

        if (cropDrag.mode === "move") {
            updateCropSelection({
                ...initial,
                x: initial.x + point.x - cropDrag.start.x,
                y: initial.y + point.y - cropDrag.start.y,
            });
            return;
        }

        if (cropDrag.mode === "resize") {
            const maxWidth = 100 - initial.x;
            const maxHeightWidth = (100 - initial.y) * START_IMAGE_ASPECT;
            const width = clamp(point.x - initial.x, 28, Math.min(maxWidth, maxHeightWidth));
            updateCropSelection({
                ...initial,
                width,
                height: width / START_IMAGE_ASPECT,
            });
            return;
        }

        const dx = Math.abs(point.x - cropDrag.start.x);
        const dyWidth = Math.abs(point.y - cropDrag.start.y) * START_IMAGE_ASPECT;
        const width = clamp(Math.max(dx, dyWidth, 28), 28, 100);
        const height = width / START_IMAGE_ASPECT;
        const x = point.x >= cropDrag.start.x ? cropDrag.start.x : cropDrag.start.x - width;
        const y = point.y >= cropDrag.start.y ? cropDrag.start.y : cropDrag.start.y - height;
        updateCropSelection({ x, y, width, height });
    };

    const stopCropDrag = () => {
        setCropDrag(null);
    };

    return (
        <Box>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>Firmenlogo</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Wird als kleines Overlay auf dem Startbild oberhalb der Adresse angezeigt. PNG, JPG oder WebP; transparente PNGs bleiben erhalten.
                </Typography>
                <Button component="label" variant="outlined" startIcon={<ImageIcon />} disabled={submitting || sortingImages}>
                    {imageSelection.logoFile || company?.company_logo_url ? "Logo ersetzen" : "Logo hochladen"}
                    <input type="file" hidden accept="image/png,image/jpeg,image/webp" onChange={(e) => {
                        imageSelection.pickLogo(e.target.files?.[0]);
                        e.target.value = "";
                    }} />
                </Button>
                {!imageSelection.removeLogo && (imageSelection.logoPreview || company?.company_logo_url) && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
                        <Box component="img" src={imageSelection.logoPreview || resolveUrl(company.company_logo_url, API_BASE)}
                            alt="Firmenlogo" sx={{ width: 140, height: 100, objectFit: "contain" }} />
                        <Button color="error" startIcon={<DeleteIcon />} disabled={submitting || sortingImages} onClick={imageSelection.clearLogo}>Logo entfernen</Button>
                    </Box>
                )}
                {imageSelection.removeLogo && <Typography variant="body2" sx={{ mt: 1 }}>Das Logo wird beim Speichern entfernt.</Typography>}
            </Paper>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>

                {/* STARTBILD */}
                <Box>
                    <Button
                        variant={imageSelection.startImageFile ? "contained" : "outlined"}
                        component="label"
                        startIcon={<ImageIcon />}
                        disabled={submitting || sortingImages}
                        fullWidth
                    >
                        {imageSelection.startImageFile ? "Startbild gewählt" : "Startbild wählen"}
                        <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={(e) => {
                                imageSelection.pickStartImage(e.target.files?.[0]);
                                e.target.value = "";
                            }}
                        />
                    </Button>

                    <Paper
                        variant="outlined"
                        onDragOver={(e) => {
                            e.preventDefault();
                            setDragActiveStart(true);
                        }}
                        onDragLeave={() => setDragActiveStart(false)}
                        onDrop={handleStartDrop}
                        sx={{
                            mt: 1,
                            height: 120,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "2px dashed",
                            borderColor: dragActiveStart ? "#c8902a" : "rgba(0,0,0,0.2)",
                        }}
                    >
                        Startbild hier hineinziehen
                    </Paper>

                    {imageSelection.startImagePreview ? (
                        <Paper variant="outlined" sx={{ mt: 1, p: 1 }}>
                            <Box sx={{ height: 150, overflow: "hidden", borderRadius: 1, backgroundColor: "#eee" }}>
                                <Box
                                    component="img"
                                    src={imageSelection.startImagePreview}
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        display: "block",
                                        ...cropImageStyle(imageSelection.startImageCrop),
                                    }}
                                />
                            </Box>
                            <Button
                                fullWidth
                                size="small"
                                variant="outlined"
                                startIcon={<CenterFocusStrongIcon />}
                                sx={{ mt: 1 }}
                                onClick={openNewStartCrop}
                            >
                                Ausschnitt festlegen
                            </Button>
                        </Paper>
                    ) : null}
                </Box>

                {/* EXTRA */}
                <Box>
                    <Button
                        variant={imageSelection.extraImageFiles.length ? "contained" : "outlined"}
                        component="label"
                        startIcon={<ImageIcon />}
                        disabled={submitting || sortingImages}
                        fullWidth
                    >
                        {imageSelection.extraImageFiles.length
                            ? `${imageSelection.extraImageFiles.length} weitere gewählt`
                            : "Weitere Bilder hinzufügen"}
                        <input
                            type="file"
                            multiple
                            hidden
                            accept="image/*"
                            onChange={(e) => {
                                imageSelection.addExtraImages(e.target.files);
                                e.target.value = "";
                            }}
                        />
                    </Button>

                    <Paper
                        variant="outlined"
                        onDragOver={(e) => {
                            e.preventDefault();
                            setDragActiveExtra(true);
                        }}
                        onDragLeave={() => setDragActiveExtra(false)}
                        onDrop={handleExtraDrop}
                        sx={{
                            mt: 1,
                            height: 120,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "2px dashed",
                            borderColor: dragActiveExtra ? "#c8902a" : "rgba(0,0,0,0.2)",
                        }}
                    >
                        Bilder hier hineinziehen
                    </Paper>

                    {imageSelection.extraImageFiles.length ? (
                        <Box sx={{ mt: 1, display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 1 }}>
                            {imageSelection.extraImageFiles.map((x, idx) => (
                                <Paper key={idx} sx={{ p: 1 }}>
                                    <Box
                                        component="img"
                                        src={x.preview}
                                        sx={{ width: "100%", height: 100, objectFit: "cover" }}
                                    />

                                    <TextField
                                        size="small"
                                        type="number"
                                        value={idx + 1}
                                        onChange={(e) => {
                                            let pos = parseInt(e.target.value);
                                            if (isNaN(pos)) return;
                                            pos = Math.max(1, Math.min(imageSelection.extraImageFiles.length, pos)) - 1;
                                            imageSelection.moveExtraSelected?.(idx, pos);
                                        }}
                                        sx={{ mt: 1 }}
                                        fullWidth
                                    />
                                </Paper>
                            ))}
                        </Box>
                    ) : null}
                </Box>
            </Box>

            {/* EXISTING */}
            <Box sx={{ mt: 2 }}>
                <Typography variant="caption">Vorhandene Bilder</Typography>

                {company?.images?.length ? (
                    <Box sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(140px,1fr))",
                        gap: 1.5,
                        mt: 1
                    }}>
                        {localOrderedImages.map((img, index) => {
                            const isStart = company?.start_image_id === img.id;
                            const url = resolveUrl(img?.url, API_BASE);

                            return (
                                <Paper
                                    key={img.id}
                                    variant="outlined"
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, img)}
                                    onDragOver={(e) => handleDragOverItem(e, img.id)}
                                    onDrop={handleDropExisting}
                                    onDragEnd={handleDropExisting}
                                    sx={{
                                        p: 1,
                                        cursor: "grab",
                                        opacity: draggedId === img.id ? 0.3 : 1,
                                        transform: dragOverId === img.id ? "scale(1.05)" : "scale(1)",
                                        transition: "all 0.2s ease"
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: "100%",
                                            height: 110,
                                            overflow: "hidden",
                                            borderRadius: 1,
                                            backgroundColor: "#eee",
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={url}
                                            sx={{
                                                width: "100%",
                                                height: "100%",
                                                display: "block",
                                                ...cropImageStyle(img),
                                            }}
                                        />
                                    </Box>

                                    <TextField
                                        size="small"
                                        type="number"
                                        value={index + 1}
                                        onChange={(e) => handleManualOrder(img.id, e.target.value)}
                                        sx={{ mt: 1 }}
                                        fullWidth
                                    />

                                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                        <IconButton onClick={() => onSetStartImage?.(img.id)}>
                                            {isStart ? <StarIcon /> : <StarBorderIcon />}
                                        </IconButton>

                                        <IconButton onClick={() => openExistingCrop(img)}>
                                            <CenterFocusStrongIcon />
                                        </IconButton>

                                        <IconButton color="error" onClick={() => onDeleteImage?.(img.id)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Box>
                ) : (
                    <Alert severity="info" sx={{ mt: 1 }}>
                        Noch keine Bilder vorhanden.
                    </Alert>
                )}
            </Box>

            {/* FLOATING PREVIEW */}
            {dragPreview && (
                <Box
                    sx={{
                        position: "fixed",
                        left: dragPreview.x + 10,
                        top: dragPreview.y + 10,
                        width: 140,
                        height: 100,
                        pointerEvents: "none",
                        zIndex: 9999,
                        boxShadow: "0 12px 30px rgba(0,0,0,0.3)",
                        borderRadius: 1,
                        overflow: "hidden",
                    }}
                >
                    <Box
                        component="img"
                        src={dragPreview.src}
                        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                </Box>
            )}

            <Dialog open={!!cropDialog} onClose={() => setCropDialog(null)} fullWidth maxWidth="sm">
                <DialogTitle>{cropDialog?.title || "Ausschnitt festlegen"}</DialogTitle>
                <DialogContent dividers>
                    {cropDialog && (
                        <Box>
                            <Box
                                sx={{
                                    height: { xs: 260, sm: 360 },
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: 1,
                                    backgroundColor: "#1f2933",
                                    overflow: "hidden",
                                    p: 1.5,
                                }}
                            >
                                <Box
                                    ref={cropImageRef}
                                    onPointerDown={(e) => handleCropPointerDown(e, "draw")}
                                    onPointerMove={handleCropPointerMove}
                                    onPointerUp={stopCropDrag}
                                    onPointerCancel={stopCropDrag}
                                    sx={{
                                        position: "relative",
                                        display: "inline-block",
                                        maxWidth: "100%",
                                        maxHeight: "100%",
                                        cursor: "crosshair",
                                        userSelect: "none",
                                        touchAction: "none",
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={cropDialog.src}
                                        sx={{
                                            display: "block",
                                            maxWidth: "100%",
                                            maxHeight: { xs: 236, sm: 336 },
                                            width: "auto",
                                            height: "auto",
                                            pointerEvents: "none",
                                        }}
                                    />
                                    <Box
                                        onPointerDown={(e) => {
                                            e.stopPropagation();
                                            handleCropPointerDown(e, "move");
                                        }}
                                        onPointerMove={handleCropPointerMove}
                                        onPointerUp={stopCropDrag}
                                        onPointerCancel={stopCropDrag}
                                        sx={{
                                            position: "absolute",
                                            left: `${cropDialog.selection.x}%`,
                                            top: `${cropDialog.selection.y}%`,
                                            width: `${cropDialog.selection.width}%`,
                                            height: `${cropDialog.selection.height}%`,
                                            border: "2px solid #fff",
                                            boxShadow:
                                                "0 0 0 1px #c8902a, 0 0 0 9999px rgba(0,0,0,0.45), 0 10px 26px rgba(0,0,0,0.25)",
                                            cursor: "move",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                inset: 0,
                                                backgroundImage:
                                                    "linear-gradient(to right, rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.5) 1px, transparent 1px)",
                                                backgroundSize: "33.333% 33.333%",
                                                pointerEvents: "none",
                                                opacity: 0.45,
                                            }}
                                        />
                                        <Box
                                            onPointerDown={(e) => {
                                                e.stopPropagation();
                                                handleCropPointerDown(e, "resize");
                                            }}
                                            onPointerMove={handleCropPointerMove}
                                            onPointerUp={stopCropDrag}
                                            onPointerCancel={stopCropDrag}
                                            sx={{
                                                position: "absolute",
                                                right: -8,
                                                bottom: -8,
                                                width: 16,
                                                height: 16,
                                                borderRadius: "50%",
                                                backgroundColor: "#c8902a",
                                                border: "2px solid #fff",
                                                cursor: "nwse-resize",
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
                                Bereich aufziehen, verschieben oder am Punkt unten rechts vergrößern.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCropDialog(null)}>Abbrechen</Button>
                    <Button variant="contained" onClick={saveCropDialog}>
                        Speichern
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
